import { useState, useEffect, useCallback, useRef } from 'react';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader } from '../../components/ui/loader';

// dnd-kit imports
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../lib/utils';

// icons
import { SearchNormal, UserSquare, Shield, TickSquare } from 'iconsax-react';

// services
import roleService from '../../services/roleService';
import UserService from '../../services/userService';

// components
import UserRoleDropZone from '../../components/users/UserRoleDropZone';
import RoleAssignmentHistory from '../../components/users/RoleAssignmentHistory';

// utils
import { getPermissionErrorMessage } from '../../utils/permissionMessages';

const UserRoleAssignment = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [roleScope, setRoleScope] = useState('ADMIN');
  const [statusFilter, setStatusFilter] = useState('');
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  const searchInputRef = useRef(null);
  const cursorPositionRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [usersResult, rolesResult] = await Promise.all([
        roleService.getUsersWithRoles({
          page: pagination.currentPage,
          limit: pagination.itemsPerPage,
          search: searchTerm,
          role: roleFilter,
          status: statusFilter
        }),
        roleService.getAllRoles({ roleType: roleScope, limit: 100 })
      ]);

      if (usersResult.success) {
        setUsers(usersResult.data.users || []);
        setPagination(prev => ({
          ...prev,
          totalPages: usersResult.data.pagination?.totalPages || 1,
          totalItems: usersResult.data.pagination?.totalItems || 0,
          itemsPerPage: usersResult.data.pagination?.itemsPerPage || prev.itemsPerPage
        }));
      }

      if (rolesResult.success) {
        const rolesData = rolesResult.data.roles || [];
        setRoles(rolesData);
      }

      if (!usersResult.success || !rolesResult.success) {
        setError('Failed to load data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to load users and roles');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, searchTerm, roleFilter, statusFilter, roleScope]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    if (pagination.currentPage !== 1) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, roleFilter, statusFilter]);

  // Restore cursor position after re-render
  useEffect(() => {
    if (searchInputRef.current) {
      const input = searchInputRef.current;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      }, 0);
    }
  }, [users]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Handle role assignment
    if (over.id.startsWith('user-') && active.id.startsWith('role-')) {
      const userId = over.id.replace('user-', '');
      const roleId = active.id.replace('role-', '');
      await assignRoleToUser(userId, roleId);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const assignRoleToUser = async (userId, roleId) => {
    try {
      const role = roles.find(r => r._id === roleId);
      if (!role) return;

      console.log('Assigning role:', { userId, roleId, roleName: role.name });
      const result = await roleService.assignRole(userId, roleId);
      console.log('Assignment result:', result);
      
      if (result.success) {
        setSuccess(`Role "${role.displayName || role.name}" assigned successfully`);
        await fetchData(); // Refresh data
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.error('Assignment failed:', result);
        setError(result.message || 'Failed to assign role');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError(error.response?.data?.message || 'Failed to assign role. Please try again.');
      }
    }
  };

  const handleBulkRoleAssignment = async (roleId) => {
    console.log('=== BULK ASSIGNMENT DEBUG ===');
    console.log('Selected users:', selectedUsers);
    console.log('Role ID:', roleId);
    
    if (selectedUsers.length === 0) {
      setError('Please select users first');
      return;
    }

    try {
      setBulkAssigning(true);
      setError('');
      
      const role = roles.find(r => r._id === roleId);
      console.log('Role found:', role?.name);
      
      const assignments = selectedUsers.map(userId => ({ userId, roleId }));
      console.log('Assignments to make:', assignments);
      // Call the bulk assignment API
      const result = await UserService.bulkAssignRoles(selectedUsers, roleId);

      console.log('Assignment result:', result);
      
      if (result.success) {
        const successful = result.data?.successful?.length || selectedUsers.length;
        const failed = result.data?.failed?.length || 0;
        
        if (successful > 0) {
          setSuccess(`Successfully assigned "${role.displayName || role.name}" to ${successful} user(s)`);
          if (failed > 0) {
            setError(`Failed to assign role to ${failed} user(s)`);
          }
          await fetchData();
          setSelectedUsers([]);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to assign roles to selected users');
        }
      } else {
        setError(result.message || 'Failed to assign roles to selected users');
      }
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to assign roles. Please try again.');
      }
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    setSelectedUsers([]); // Clear selections when changing pages
  };

  const handleViewHistory = (user) => {
    setSelectedUserForHistory(user);
    setOpenHistoryDialog(true);
  };

  const getFilteredRoles = () => {
    return roles.filter(role => role.isActive);
  };

  const getUserStats = () => {
    const totalUsers = pagination.totalItems;
    const selectedCount = selectedUsers.length;
    const roleDistribution = roles.reduce((acc, role) => {
      acc[role.name] = 0; // Backend would need to provide this data
      return acc;
    }, {});

    return { totalUsers, selectedCount, roleDistribution };
  };



  const filteredRoles = getFilteredRoles();
  const stats = getUserStats();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          User Role Assignment
        </h1>
        <p className="text-sm text-muted-foreground">
          Drag and drop roles to users or use bulk operations for efficient role management
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 text-emerald-700 border-emerald-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-primary">
                  {loading ? '...' : stats.totalUsers}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Total Users
                </p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <UserSquare size={24} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-purple-600">
                  {loading ? '...' : stats.selectedCount}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Selected Users
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TickSquare size={24} className="text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-600">
                  {loading ? '...' : filteredRoles.length}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Available Roles
                </p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Shield size={24} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-sky-600">
                  {loading ? '...' : Object.keys(stats.roleDistribution).length}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Role Types
                </p>
              </div>
              <div className="p-2 bg-sky-100 rounded-lg">
                <Shield size={24} className="text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3 relative">
              <SearchNormal className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                ref={searchInputRef}
                placeholder="Search users..."
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  cursorPositionRef.current = e.target.selectionStart;
                }}
                onSelect={(e) => {
                  cursorPositionRef.current = e.target.selectionStart;
                }}
                className="pl-9 w-full bg-background"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto items-center">
              <div className="w-full sm:w-48">
                <Select value={roleScope} onValueChange={(val) => setRoleScope(val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Role Scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin Roles</SelectItem>
                    <SelectItem value="CLIENT">Client Roles</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-48">
                <Select value={roleFilter || "all"} onValueChange={(val) => setRoleFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {filteredRoles.map((role) => (
                      <SelectItem key={role._id} value={role.name}>
                        {role.displayName || role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-48">
                <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <Checkbox
                  id="selectAll"
                  checked={selectedUsers.length > 0 && selectedUsers.length < users.length ? "indeterminate" : selectedUsers.length === users.length && users.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="selectAll" className="text-sm font-medium text-foreground cursor-pointer select-none">
                  Select All
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="grid grid-cols-12 gap-6">
          {/* Available Roles */}
          <div className="col-span-12 md:col-span-3">
            <Card className="h-full">
              <CardContent className="p-4 flex flex-col h-full">
                <h3 className="text-base font-semibold text-foreground mb-1">
                  Available Roles
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Drag roles to users to assign them
                </p>
                
                <SortableContext items={filteredRoles.map(role => `role-${role._id}`)} strategy={verticalListSortingStrategy}>
                  <div
                    className="min-h-[250px] p-2 border-2 border-dashed border-slate-200 rounded-lg flex-1 bg-slate-50/30"
                  >
                    {filteredRoles.map((role) => (
                      <DraggableRole 
                        key={role._id} 
                        role={role} 
                        selectedUsers={selectedUsers} 
                        onBulkAssign={handleBulkRoleAssignment} 
                        bulkAssigning={bulkAssigning} 
                      />
                    ))}
                  </div>
                </SortableContext>
              </CardContent>
            </Card>
          </div>

          {/* Users List */}
          <div className="col-span-12 md:col-span-9">
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-semibold text-foreground">
                    Users ({pagination.totalItems})
                  </h3>
                  {pagination.totalPages > 1 && (
                    <span className="text-xs text-muted-foreground">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Select users and drag roles to them, or use bulk operations
                </p>

                {loading ? (
                  <Loader message="Loading users and roles data..." className="min-h-[300px]" />
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {users.map((user) => (
                        <UserRoleDropZone
                          key={user._id}
                          user={user}
                          isSelected={selectedUsers.includes(user._id)}
                          onSelect={() => handleUserSelection(user._id)}
                          onViewHistory={() => handleViewHistory(user)}
                          roles={roles}
                        />
                      ))}
                    </div>

                    {users.length === 0 && (
                      <div className="text-center py-10 text-sm text-muted-foreground">
                        No users found matching the current filters
                      </div>
                    )}

                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-100">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
                          disabled={pagination.currentPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-xs font-medium text-muted-foreground">
                          Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <div
              className="p-3 border border-primary bg-primary/10 rounded-lg shadow-md cursor-grabbing font-medium text-xs text-primary"
            >
              {activeId.startsWith('role-') 
                ? filteredRoles.find(r => `role-${r._id}` === activeId)?.displayName || 'Role'
                : 'Dragging...'}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Role Assignment History Dialog */}
      <Dialog open={openHistoryDialog} onOpenChange={(open) => { if (!open) setOpenHistoryDialog(false); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Role Assignment History - {selectedUserForHistory?.name}
            </DialogTitle>
            <DialogDescription>
              Audit records for user role assignments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <RoleAssignmentHistory user={selectedUserForHistory} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Draggable Role Component
const DraggableRole = ({ role, selectedUsers, onBulkAssign, bulkAssigning }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `role-${role._id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        "mb-2.5 p-3 border border-slate-200 rounded-lg bg-background hover:shadow-sm transition-shadow flex flex-col gap-2",
        isDragging && "shadow-md border-primary"
      )}
    >
      <div
        {...listeners}
        className="cursor-grab active:cursor-grabbing hover:bg-slate-50 p-1.5 rounded transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-foreground">
            {role.displayName || role.name}
          </span>
          <Badge className={cn("text-[10px] font-semibold border-none px-2 py-0.5", 
            role.isSystem ? "bg-primary/10 text-primary hover:bg-primary/10" : "bg-slate-100 text-slate-700 hover:bg-slate-100"
          )}>
            {role.isSystem ? 'System' : 'Custom'}
          </Badge>
        </div>
      </div>
      
      {selectedUsers.length > 0 && (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            console.log('Bulk assign button clicked for role:', role._id);
            e.stopPropagation();
            onBulkAssign(role._id);
          }}
          disabled={bulkAssigning}
          className="w-full text-xs font-semibold"
        >
          {bulkAssigning ? 'Assigning...' : `Assign to ${selectedUsers.length} user(s)`}
        </Button>
      )}
    </div>
  );
};

export default UserRoleAssignment;